import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, FiltroPill, Pagination, Pill, type Column } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { fmtBRL } from "../../lib/banco-propostas";
import { bancoPodeVerContratos, readBancoPermissoesDaSessao } from "../../lib/banco-perms";
import { aplicarFiltros, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { matchAnyKeys } from "../../lib/text-search";
import { useFiltros } from "../../lib/use-filtros";

// Somente leitura: convênios são criados/geridos pela averbadora. O banco vê
// TODOS os convênios dele aqui e escolhe em qual operar.
//
// Era uma grade de cards, um por convênio. Com dez ou mais a tela virava um
// mosaico sem ordem de leitura, e uma consignatária grande opera em dezenas de
// órgãos. Agora o convênio em que se opera fica num painel próprio, com os
// números da carteira, e todos os convênios vão para uma tabela com busca,
// filtro de UF e paginação.
//
// Os números de carteira saem de `GET /contratos`, que é escopado ao convênio
// ATIVO. Por isso só o painel do ativo tem números: pedir a carteira de todos
// usaria o opt-in `incluir_todos_convenios`, que não recorta município
// (PENDENCIAS 21).
type Convenio = Awaited<ReturnType<typeof atlas.banco.convenios>>["convenios"][number];

interface Linha {
  id: string;
  orgao: string;
  convenio: string;
  uf: string;
  ativo: boolean;
  exigeCcb: boolean;
  maxParcelas: number | null;
  taxaMaxAm: number | null;
  vigenciaInicio: string | null;
  vigenciaFim: string | null;
}

interface Carteira {
  contratosAtivos: number;
  matriculasUnicas: number;
  volumeAtivo: number;
  ticketMedio: number;
  semValor: number;
  aguardando: number;
}

const linhaDe = (cv: Convenio, activeId: string | undefined): Linha => ({
  id: cv.id,
  orgao: cv.prefeitura,
  convenio: cv.nome,
  uf: cv.uf,
  ativo: cv.id === activeId,
  exigeCcb: cv.exigeCcb,
  maxParcelas: cv.maxParcelas,
  taxaMaxAm: cv.taxaMaxAm,
  vigenciaInicio: cv.vigenciaInicio,
  vigenciaFim: cv.vigenciaFim,
});

export function BancoConvenios() {
  const qc = useQueryClient();
  // `GET /contratos` exige `carteira`/`propostas`. Quem nao tem (ex.: preset
  // "Consulta de margem") ve so' o convenio — contador zerado passaria por dado.
  const podeVerContratos = useMemo(() => bancoPodeVerContratos(readBancoPermissoesDaSessao()), []);
  const conveniosQ = useQuery({
    queryKey: ["banco", "convenios"],
    queryFn: () => atlas.banco.convenios(),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const contratosQ = useQuery({
    queryKey: ["banco", "contratos", "convenios-view"],
    queryFn: () => atlas.banco.contratos(),
    enabled: podeVerContratos,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  // Mesma troca do seletor do topo: grava o convenio ativo e recarrega tudo
  // que e' escopado por ele.
  const operar = useMutation({
    mutationFn: (id: string) => atlas.banco.setConvenioAtivo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banco"] }),
  });

  const activeId = conveniosQ.data?.activeId;

  // Ordem alfabetica por orgao. O ativo NAO sobe para o topo da tabela: ele ja
  // tem o painel proprio, e uma linha que muda de lugar ao trocar de convenio
  // faria o operador perder a posicao na lista.
  const base = useMemo(
    () =>
      (conveniosQ.data?.convenios ?? [])
        .map((cv) => linhaDe(cv, activeId))
        .sort((a, b) => a.orgao.localeCompare(b.orgao, "pt-BR") || a.convenio.localeCompare(b.convenio, "pt-BR")),
    [conveniosQ.data, activeId],
  );
  const ativo = base.find((l) => l.ativo) ?? null;

  const carteira = useMemo((): Carteira | null => {
    if (!ativo) return null;
    const doConv = (contratosQ.data?.contratos ?? []).filter((ct) => ct.convenioId === ativo.id);
    const emFolha = doConv.filter((ct) => {
      const s = ct.situacao.toLowerCase();
      return s === "ativo" || s.includes("averb");
    });
    // Ticket medio so' sobre quem tem valor financiado informado: o contrato
    // legado sem dados financeiros chega com 0 e puxava a media pra baixo.
    const comValor = emFolha.filter((ct) => (ct.valorFinanciado || 0) > 0);
    const volumeAtivo = comValor.reduce((a, ct) => a + ct.valorFinanciado, 0);
    return {
      contratosAtivos: emFolha.length,
      matriculasUnicas: new Set(emFolha.map((ct) => ct.matricula)).size,
      volumeAtivo,
      ticketMedio: comValor.length ? volumeAtivo / comValor.length : 0,
      semValor: emFolha.length - comValor.length,
      aguardando: doConv.filter((ct) => ct.situacao.toLowerCase().includes("aguard")).length,
    };
  }, [ativo, contratosQ.data]);

  const [busca, setBusca] = useState("");
  const [ufs, setUfs] = useState<string[]>([]);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  const termo = busca.trim();
  const filtros = useMemo(() => ({
    uf: ufs.length ? (l: Linha) => ufs.includes(l.uf) : null,
    busca: termo ? (l: Linha) => matchAnyKeys(l, termo, ["orgao", "convenio", "id"]) : null,
  }), [ufs, termo]);
  const linhas = useMemo(() => aplicarFiltros(base, filtros), [base, filtros]);
  const opcoesUf = useMemo(
    () => [...new Set(base.map((l) => l.uf))].sort().map((uf) => ({ value: uf, label: uf })),
    [base],
  );

  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "uf", ligado: ufs.length > 0, limpar: () => setUfs([]) },
    ],
    setPagina,
  );
  const { filtrar } = filtrosUi;
  const daPagina = recortar(linhas, pagina, porPagina);

  const columns: Column<Linha>[] = [
    {
      key: "orgao",
      header: "Órgão ou entidade",
      wrap: true,
      render: (l) => (
        <>
          <div style={{ fontWeight: 600 }}>{l.orgao}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{l.convenio}</div>
        </>
      ),
    },
    { key: "uf", header: "UF", mono: true, render: (l) => l.uf },
    { key: "prazo", header: "Prazo máximo", align: "right", render: (l) => (l.maxParcelas == null ? <Apagado texto="teto da plataforma" /> : `${l.maxParcelas}x`) },
    { key: "cet", header: "Teto de CET", align: "right", render: (l) => (l.taxaMaxAm == null ? <Apagado texto="não definido" /> : `${l.taxaMaxAm.toLocaleString("pt-BR")}% a.m.`) },
    { key: "vigencia", header: "Vigência", render: (l) => vigencia(l.vigenciaInicio, l.vigenciaFim) },
    // So' o que a API valida. O 2FA da averbadora e' configuravel mas nenhum
    // handler o consulta (regras-convenio.ts, `informativas`).
    { key: "ccb", header: "CCB", render: (l) => (l.exigeCcb ? "obrigatória" : <Apagado texto="não exigida" />) },
    {
      key: "acoes",
      header: "",
      render: (l) => (
        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
          <Link to={`/banco/convenios/regras#${l.id}`} style={{ fontSize: 13, whiteSpace: "nowrap" }}>Regras</Link>
          {l.ativo ? (
            <Pill variant="pendente">operando agora</Pill>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => operar.mutate(l.id)} disabled={operar.isPending}>
              Operar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Convênios
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Seus convênios</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 680 }}>
          Os órgãos e entidades em que a sua consignatária tem convênio. Os convênios são cadastrados e mantidos
          pela averbadora; aqui você consulta as regras de cada um e escolhe em qual operar.{" "}
          <Link to="/banco/convenios/regras">Ver as regras de todos os convênios →</Link>
        </p>
      </header>

      {conveniosQ.isPending ? (
        <div style={{ color: "var(--text-muted)" }}>Carregando convênios…</div>
      ) : conveniosQ.error ? (
        <div style={{ color: "var(--danger-500)" }}>Erro ao carregar convênios.</div>
      ) : base.length === 0 ? (
        <div style={{ color: "var(--text-muted)" }}>Nenhum convênio ativo para a sua consignatária.</div>
      ) : (
        <>
          {ativo ? <PainelAtivo l={ativo} carteira={podeVerContratos ? carteira : null} /> : null}

          <FilterBar
            layout="linha"
            searchValue={busca}
            onSearchChange={filtrar(setBusca)}
            searchPlaceholder="Buscar por órgão, convênio ou código"
            {...filtrosUi.propsFilterBar}
          >
            {opcoesUf.length > 1 ? (
              <FiltroPill label="UF" opcoes={opcoesUf} selecionados={ufs} onChange={filtrar(setUfs)} />
            ) : null}
          </FilterBar>

          <DataTable
            caption="Convênios da consignatária"
            columns={columns}
            rows={daPagina.itens}
            rowKey={(l) => l.id}
            emptyState={
              <EmptyState
                motivo={motivoVazio(base.length, linhas.length)}
                filtroVazio="Nenhum convênio com esses filtros."
                semDados="Nenhum convênio ativo para a sua consignatária."
                onLimpar={filtrosUi.limparFiltros}
              />
            }
          />
          <Pagination
            total={daPagina.total}
            pagina={daPagina.pagina}
            porPagina={porPagina}
            onPagina={setPagina}
            onPorPagina={setPorPagina}
            rotulo="convênios"
          />
        </>
      )}
    </div>
  );
}

/** O convenio em que se opera: as regras e, para quem pode ver, a carteira dele. */
function PainelAtivo({ l, carteira }: { l: Linha; carteira: Carteira | null }) {
  return (
    <section
      data-testid={`convenio-${l.id}`}
      style={{ background: "var(--bg-elev)", border: "1px solid var(--gold-500)", borderRadius: 12, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--gold-500)" }}>
          Operando agora
        </span>
        <h2 style={{ margin: 0, fontSize: "1.15rem" }}>{l.orgao}</h2>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{l.convenio} · {l.uf}</span>
        <Link to={`/banco/convenios/regras#${l.id}`} style={{ marginLeft: "auto", fontSize: 13 }}>Regras do convênio</Link>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <Fato rotulo="Prazo máximo" valor={l.maxParcelas == null ? "teto da plataforma" : `${l.maxParcelas} parcelas`} />
        <Fato rotulo="Teto de CET" valor={l.taxaMaxAm == null ? "não definido" : `${l.taxaMaxAm.toLocaleString("pt-BR")}% a.m.`} />
        <Fato rotulo="Vigência" valor={vigencia(l.vigenciaInicio, l.vigenciaFim)} />
        <Fato rotulo="CCB" valor={l.exigeCcb ? "obrigatória" : "não exigida"} />
      </div>

      {carteira ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          <Fato rotulo="Contratos ativos" valor={`${carteira.contratosAtivos}`} />
          <Fato rotulo="Matrículas únicas" valor={`${carteira.matriculasUnicas}`} />
          <Fato rotulo="Volume ativo" valor={fmtBRL(carteira.volumeAtivo)} />
          <Fato
            rotulo="Ticket médio"
            valor={fmtBRL(carteira.ticketMedio)}
            hint={carteira.semValor > 0 ? `sem contar ${carteira.semValor} sem valor informado` : undefined}
          />
          <Fato rotulo="Aguardando análise" valor={`${carteira.aguardando}`} accent={carteira.aguardando > 0 ? "var(--gold-500)" : undefined} />
        </div>
      ) : null}
    </section>
  );
}

function vigencia(inicio: string | null, fim: string | null): string {
  const d = (s: string) => new Date(`${s.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR");
  if (!inicio && !fim) return "não definida";
  if (inicio && !fim) return `desde ${d(inicio)}`;
  if (!inicio && fim) return `até ${d(fim)}`;
  return `${d(inicio!)} a ${d(fim!)}`;
}

/** Valor ausente: o padrao que vale no lugar dele, em tom apagado. */
function Apagado({ texto }: { texto: string }) {
  return <span style={{ color: "var(--text-dim)" }}>{texto}</span>;
}

function Fato({ rotulo, valor, accent, hint }: { rotulo: string; valor: string; accent?: string; hint?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{rotulo}</span>
      <span style={{ fontSize: 15, fontWeight: 600, color: accent ?? "var(--text)" }}>{valor}</span>
      {hint ? <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{hint}</span> : null}
    </div>
  );
}
